import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { FormInput } from '@/components/ui/form-input';
import { FormSelect } from '@/components/ui/form-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, toast } from '@/components/ui';
import { createCrewSchema, type CreateCrewInput } from '@/types';
import {
  CREW_PROBABILITY,
  CREW_FINANCING_TYPE,
  CREW_FINANCING_TYPE_LABELS,
  CREW_STATUS,
  CREW_STATUS_LABELS,
} from '@/types';
import { PROBABILITY_SELECT_OPTIONS } from '@/lib/crew-probability-constants';
import { useProductsSelect } from '@/hooks/useProductsSelect';
import { useVehiclesSelect } from '@/hooks/useVehiclesSelect';
import { useAssociationsSelect } from '@/hooks/useAssociationsSelect';
import { useDiscountsSelect } from '@/hooks/useDiscountsSelect';
import { useBpiLookup, type BpiContact, type BpiLookupResult } from '@/hooks/useBpiLookup';
import { useCrewNumberAvailability } from '@/hooks/useCrewNumberAvailability';
import {
  describeCrewNumberCheck,
  isCrewNumberAssistUnavailable,
  parseCrewNumberInt,
  type CrewNumberCheck,
  type CrewNumberDescription,
} from '@/lib/crewNumberAvailability';
import { AlertTriangle, CheckCircle, Loader2, Users, Search, Hash } from 'lucide-react';

/**
 * Données de création d'équipage avec les contacts BPI associés
 */
export interface CreateCrewWithBpiData {
  crewData: CreateCrewInput;
  bpiContacts: BpiContact[];
  allowReservedNumber: boolean;
  /** true uniquement pour le numéro issu du bouton : allocation atomique côté RPC. */
  assignNumber: boolean;
}

/** Retour minimal attendu du créateur : le numéro réellement attribué par le serveur. */
export interface CreatedCrewSummary {
  crew_number: string | null;
}

interface CreateCrewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateCrewWithBpiData) => Promise<CreatedCrewSummary | void>;
}

// Options pour le select de statut
const STATUS_SELECT_OPTIONS = [
  {
    value: CREW_STATUS.BPI_PAYMENT_PENDING,
    label: CREW_STATUS_LABELS[CREW_STATUS.BPI_PAYMENT_PENDING],
  },
  { value: CREW_STATUS.PRE_REGISTERED, label: CREW_STATUS_LABELS[CREW_STATUS.PRE_REGISTERED] },
  { value: CREW_STATUS.BED_INCOMPLETE, label: CREW_STATUS_LABELS[CREW_STATUS.BED_INCOMPLETE] },
  { value: CREW_STATUS.BED_COMPLETE, label: CREW_STATUS_LABELS[CREW_STATUS.BED_COMPLETE] },
  {
    value: CREW_STATUS.CANCELLATION_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.CANCELLATION_COMPLIANT],
  },
  {
    value: CREW_STATUS.CANCELLATION_NON_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.CANCELLATION_NON_COMPLIANT],
  },
  {
    value: CREW_STATUS.POSTPONEMENT_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.POSTPONEMENT_COMPLIANT],
  },
  {
    value: CREW_STATUS.POSTPONEMENT_NON_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.POSTPONEMENT_NON_COMPLIANT],
  },
];

// Types de recherche BPI
type SearchMode = 'bpi_code' | 'crew_name';

export function CreateCrewModal({ open, onOpenChange, onSubmit }: CreateCrewModalProps) {
  const { products } = useProductsSelect();
  const { vehicles } = useVehiclesSelect();
  const { associations } = useAssociationsSelect();
  const { discounts } = useDiscountsSelect();
  const { peekNext, check: checkCrewNumber } = useCrewNumberAvailability();

  // Hook de recherche BPI
  const {
    lookup: lookupBpi,
    lookupByCrewName,
    reset: resetBpi,
    loading: bpiLoading,
    result: bpiResult,
    multipleResults,
  } = useBpiLookup();

  // State pour la recherche
  const [searchMode, setSearchMode] = useState<SearchMode>('bpi_code');
  const [searchInput, setSearchInput] = useState('');
  const [searched, setSearched] = useState(false);
  const [selectedBpiResult, setSelectedBpiResult] = useState<BpiLookupResult | null>(null);
  const [numberDescription, setNumberDescription] = useState<CrewNumberDescription | null>(null);
  const [numberChecking, setNumberChecking] = useState(false);
  const [autoPreview, setAutoPreview] = useState<{
    productId: string;
    crewNumberInt: number;
  } | null>(null);
  // Aperçu du prochain numéro pour le produit sélectionné (amorcé au choix du
  // produit) : alimente l'aide « Vide = … » et détecte une divergence à la création.
  const [nextPreview, setNextPreview] = useState<{
    productId: string;
    crewNumberInt: number;
    crewNumber: string;
  } | null>(null);
  const [reservedConfirmation, setReservedConfirmation] = useState<{
    data: CreateCrewInput;
    check: CrewNumberCheck;
  } | null>(null);
  const numberRequestRef = useRef(0);
  const previousProductRef = useRef<string | undefined>(undefined);
  // Produit sans config de numérotation : on mémorise le message pour ne pas
  // rejouer la RPC (en erreur) à chaque frappe.
  const assistUnavailableRef = useRef<{ productId: string; message: string } | null>(null);

  const methods = useForm<CreateCrewInput>({
    resolver: zodResolver(createCrewSchema),
    defaultValues: {
      product_id: '',
      probability: CREW_PROBABILITY.MEDIUM,
      financing_type: CREW_FINANCING_TYPE.SELF_FINANCING,
      status: CREW_STATUS.PRE_REGISTERED,
      has_derogation: false,
      organizes_pvd: false,
    },
  });

  const {
    control,
    formState: { isSubmitting },
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
  } = methods;
  const productId = watch('product_id');
  const crewNumberRaw = watch('crew_number');

  // Recherche avec debounce
  useEffect(() => {
    if (!searchInput.trim()) {
      resetBpi();
      setSearched(false);
      setSelectedBpiResult(null);
      return;
    }

    const timer = setTimeout(() => {
      if (searchMode === 'bpi_code') {
        lookupBpi(searchInput);
      } else {
        lookupByCrewName(searchInput);
      }
      setSearched(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, searchMode, lookupBpi, lookupByCrewName, resetBpi]);

  // Pré-remplir les champs quand un BPI est trouvé (recherche par code)
  useEffect(() => {
    if (bpiResult?.found && searchMode === 'bpi_code') {
      // Pré-remplir le nom de l'équipage si disponible
      if (bpiResult.crewName) {
        setValue('name', bpiResult.crewName);
      }
      // Pré-remplir le produit si disponible
      if (bpiResult.productId) {
        // shouldValidate : efface « Le produit est requis » si l'erreur était déjà affichée
        setValue('product_id', bpiResult.productId, { shouldValidate: true });
      }
    }
  }, [bpiResult, searchMode, setValue]);

  // Pré-remplir quand on sélectionne un résultat multiple
  useEffect(() => {
    if (selectedBpiResult) {
      if (selectedBpiResult.crewName) {
        setValue('name', selectedBpiResult.crewName);
      }
      if (selectedBpiResult.productId) {
        setValue('product_id', selectedBpiResult.productId, { shouldValidate: true });
      }
    }
  }, [selectedBpiResult, setValue]);

  // Réinitialiser à la fermeture du modal
  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setSearched(false);
      setSelectedBpiResult(null);
      setSearchMode('bpi_code');
      setNumberDescription(null);
      setNumberChecking(false);
      setAutoPreview(null);
      setNextPreview(null);
      setReservedConfirmation(null);
      assistUnavailableRef.current = null;
      resetBpi();
    }
  }, [open, resetBpi]);

  useEffect(() => {
    if (previousProductRef.current !== undefined && previousProductRef.current !== productId) {
      if (autoPreview) setValue('crew_number', undefined);
      setAutoPreview(null);
      setNumberDescription(null);
      assistUnavailableRef.current = null;
    }
    previousProductRef.current = productId;
  }, [autoPreview, productId, setValue]);

  // Amorçage au choix du produit : un aperçu non mutant du prochain numéro.
  // Un produit sans configuration de numérotation est mémorisé ici, avant
  // toute saisie, pour que l'aide « Vide = … » ne promette pas une
  // attribution automatique impossible.
  useEffect(() => {
    if (!productId) {
      setNextPreview(null);
      return;
    }
    let cancelled = false;
    peekNext(productId)
      .then((result) => {
        if (cancelled) return;
        setNextPreview({
          productId,
          crewNumberInt: result.crew_number_int,
          crewNumber: result.crew_number,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setNextPreview(null);
        if (isCrewNumberAssistUnavailable(error) && error instanceof Error) {
          assistUnavailableRef.current = { productId, message: error.message };
          setNumberDescription({ tone: 'warn', text: error.message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [peekNext, productId]);

  useEffect(() => {
    const requestId = ++numberRequestRef.current;
    const requestedInt = parseCrewNumberInt(crewNumberRaw);

    if (!productId) {
      setNumberChecking(false);
      setNumberDescription(null);
      return;
    }
    if (assistUnavailableRef.current?.productId === productId) {
      setNumberChecking(false);
      setNumberDescription({ tone: 'warn', text: assistUnavailableRef.current.message });
      return;
    }
    if (!crewNumberRaw?.trim()) {
      setNumberChecking(false);
      const previewLabel =
        nextPreview && nextPreview.productId === productId
          ? ` (${nextPreview.crewNumber} avec le bouton ci-dessus)`
          : '';
      setNumberDescription({
        tone: 'warn',
        text: `Vide = équipage créé sans numéro${previewLabel}`,
      });
      return;
    }
    if (requestedInt === null) {
      setNumberChecking(false);
      setNumberDescription({ tone: 'warn', text: 'Pas de vérification possible' });
      return;
    }
    if (autoPreview?.productId === productId && autoPreview.crewNumberInt === requestedInt) {
      // Numéro issu du bouton « Prochain numéro dispo » : déjà vérifié, pas de
      // nouvel appel (évite le clignotement du message).
      setNumberChecking(false);
      setNumberDescription({
        tone: 'ok',
        text: 'Prochain numéro proposé (confirmé définitivement à la création)',
      });
      return;
    }

    setNumberChecking(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkCrewNumber(productId, requestedInt);
        if (numberRequestRef.current === requestId) {
          setNumberDescription(describeCrewNumberCheck(result, requestedInt));
        }
      } catch (error) {
        if (isCrewNumberAssistUnavailable(error) && error instanceof Error) {
          assistUnavailableRef.current = { productId, message: error.message };
        }
        if (numberRequestRef.current === requestId) {
          setNumberDescription({
            tone: 'warn',
            text: error instanceof Error ? error.message : 'Vérification indisponible',
          });
        }
      } finally {
        if (numberRequestRef.current === requestId) setNumberChecking(false);
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [autoPreview, checkCrewNumber, crewNumberRaw, nextPreview, productId]);

  // Handler pour le changement d'input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setSelectedBpiResult(null);
  }, []);

  // Handler pour changer le mode de recherche
  const handleModeChange = useCallback(
    (mode: SearchMode) => {
      setSearchMode(mode);
      setSearchInput('');
      setSearched(false);
      setSelectedBpiResult(null);
      resetBpi();
    },
    [resetBpi]
  );

  // Handler pour sélectionner un résultat multiple
  const handleSelectBpiResult = useCallback((result: BpiLookupResult) => {
    setSelectedBpiResult(result);
    // Mettre à jour l'input avec le code BPI sélectionné
    if (result.bpiCode) {
      setSearchInput(result.bpiCode);
    }
  }, []);

  // Obtenir les contacts effectifs (du résultat sélectionné ou du résultat unique)
  const getEffectiveContacts = useCallback((): BpiContact[] => {
    if (selectedBpiResult) {
      return selectedBpiResult.contacts;
    }
    if (bpiResult?.found) {
      return bpiResult.contacts;
    }
    return [];
  }, [selectedBpiResult, bpiResult]);

  // Obtenir le code BPI effectif
  const getEffectiveBpiCode = useCallback((): string => {
    if (selectedBpiResult?.bpiCode) {
      return selectedBpiResult.bpiCode;
    }
    if (bpiResult?.bpiCode) {
      return bpiResult.bpiCode;
    }
    return searchInput.trim();
  }, [selectedBpiResult, bpiResult, searchInput]);

  async function submitCrew(data: CreateCrewInput, allowReservedNumber: boolean) {
    clearErrors('crew_number');
    const requestedInt = parseCrewNumberInt(data.crew_number);
    const isAutoPreview =
      autoPreview !== null &&
      autoPreview.productId === productId &&
      autoPreview.crewNumberInt === requestedInt;
    // Numéro laissé vide = équipage sans numéro (cas courant des pré-inscrits).
    // L'allocation automatique n'a lieu que sur le numéro issu du bouton, où
    // elle reste atomique côté RPC (le compteur avance de 1, comme au paiement).
    const shouldAllocateAutomatically = Boolean(productId) && isAutoPreview;

    let normalizedCrewNumber = shouldAllocateAutomatically ? undefined : data.crew_number;
    const assistUnavailable = assistUnavailableRef.current?.productId === productId;
    if (productId && requestedInt !== null && !shouldAllocateAutomatically && !assistUnavailable) {
      try {
        const result = await checkCrewNumber(productId, requestedInt);
        if (result.status === 'taken') {
          setNumberDescription(null);
          setError('crew_number', {
            type: 'manual',
            message: describeCrewNumberCheck(result, requestedInt).text,
          });
          return;
        }
        if (result.status === 'reserved' && !allowReservedNumber) {
          setReservedConfirmation({ data, check: result });
          return;
        }
        normalizedCrewNumber = result.crew_number;
      } catch (error) {
        if (!isCrewNumberAssistUnavailable(error)) {
          toast.error('Vérification du numéro impossible', {
            description:
              error instanceof Error ? error.message : 'Réessayez dans quelques instants.',
          });
          return;
        }
        // Produit sans configuration de numérotation : la RPC accepte la
        // saisie manuelle telle quelle (comportement historique), sans contrôle.
        if (error instanceof Error) {
          assistUnavailableRef.current = { productId, message: error.message };
        }
        normalizedCrewNumber = data.crew_number;
      }
    }

    // Inclure le bpi_code dans les données et passer les contacts trouvés
    const crewData: CreateCrewInput = {
      ...data,
      crew_number: normalizedCrewNumber,
      bpi_code: getEffectiveBpiCode() || undefined,
    };

    let created: CreatedCrewSummary | void;
    try {
      created = await onSubmit({
        crewData,
        bpiContacts: getEffectiveContacts(),
        allowReservedNumber,
        assignNumber: shouldAllocateAutomatically,
      });
    } catch (error) {
      toast.error("Création de l'équipage impossible", {
        description: error instanceof Error ? error.message : 'Réessayez dans quelques instants.',
      });
      return;
    }

    // Allocation automatique : le serveur a pu attribuer un autre numéro que
    // celui affiché si le compteur a bougé entre l'aperçu et la création.
    if (shouldAllocateAutomatically && created?.crew_number && autoPreview) {
      const expectedInt = autoPreview.crewNumberInt;
      const createdInt = parseCrewNumberInt(created.crew_number);
      if (createdInt !== null && createdInt !== expectedInt) {
        toast.warning('Numéro différent de l’aperçu', {
          description: `Le numéro ${expectedInt} a été pris entre-temps : l’équipage a reçu le numéro ${created.crew_number}.`,
        });
      }
    }

    reset();
    setSearchInput('');
    setSearched(false);
    setSelectedBpiResult(null);
    resetBpi();
    setAutoPreview(null);
    setNumberDescription(null);
    onOpenChange(false);
  }

  async function handleFormSubmit(data: CreateCrewInput) {
    await submitCrew(data, false);
  }

  async function handlePeekNext() {
    if (!productId) return;
    const requestedProductId = productId;
    const requestId = ++numberRequestRef.current;
    setNumberChecking(true);
    try {
      const result = await peekNext(requestedProductId);
      if (
        numberRequestRef.current !== requestId ||
        previousProductRef.current !== requestedProductId
      ) {
        return;
      }
      setAutoPreview({ productId: requestedProductId, crewNumberInt: result.crew_number_int });
      setNextPreview({
        productId: requestedProductId,
        crewNumberInt: result.crew_number_int,
        crewNumber: result.crew_number,
      });
      setValue('crew_number', result.crew_number, { shouldDirty: true, shouldValidate: true });
    } catch (error) {
      if (numberRequestRef.current !== requestId) return;
      toast.error('Prochain numéro indisponible', {
        description: error instanceof Error ? error.message : 'Réessayez dans quelques instants.',
      });
    } finally {
      if (numberRequestRef.current === requestId) setNumberChecking(false);
    }
  }

  // Déterminer si on affiche les résultats multiples
  const showMultipleResults =
    searchMode === 'crew_name' &&
    searched &&
    !bpiLoading &&
    multipleResults?.found &&
    !selectedBpiResult;

  // Déterminer si on affiche le résultat unique
  const showSingleResult =
    (searchMode === 'bpi_code' && searched && !bpiLoading && searchInput.trim()) ||
    selectedBpiResult;

  // Le résultat à afficher (sélectionné ou trouvé)
  const displayResult = selectedBpiResult || bpiResult;

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title="Créer un équipage"
      size="xl"
      footer={
        <ModalFooter
          onCancel={() => onOpenChange(false)}
          cancelLabel="Annuler"
          submitLabel="Créer"
          isSubmitting={isSubmitting}
          formId="create-crew-form"
        />
      }
    >
      <FormProvider {...methods}>
        <form
          id="create-crew-form"
          onSubmit={methods.handleSubmit(handleFormSubmit)}
          className="space-y-6 px-1"
        >
          {/* Recherche BPI / Nom d'équipage */}
          <div className="space-y-3">
            {/* Toggle mode de recherche */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={searchMode === 'bpi_code' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModeChange('bpi_code')}
                className="flex items-center gap-1.5"
              >
                <Hash className="h-3.5 w-3.5" />
                Par code BPI
              </Button>
              <Button
                type="button"
                variant={searchMode === 'crew_name' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModeChange('crew_name')}
                className="flex items-center gap-1.5"
              >
                <Search className="h-3.5 w-3.5" />
                Par nom d'équipage
              </Button>
            </div>

            {/* Champ de recherche */}
            <div className="relative">
              <label
                htmlFor="search_input"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {searchMode === 'bpi_code' ? 'Code BPI (optionnel)' : "Nom d'équipage (optionnel)"}
              </label>
              <div className="relative mt-1.5">
                <input
                  id="search_input"
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder={
                    searchMode === 'bpi_code' ? 'BPI-XXXXXX' : 'Ex: Les Cigales du Désert'
                  }
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
                {bpiLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {searchMode === 'bpi_code'
                  ? 'Renseignez un code BPI pour associer automatiquement les contacts et pré-remplir les champs'
                  : "Recherchez par nom d'équipage si vous n'avez pas le code BPI"}
              </p>
            </div>

            {/* Résultats multiples (recherche par nom) */}
            {showMultipleResults && multipleResults && (
              <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Search className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {multipleResults.results.length} BPI trouvé
                    {multipleResults.results.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {multipleResults.results.map((result) => (
                    <button
                      key={result.bpiCode}
                      type="button"
                      onClick={() => handleSelectBpiResult(result)}
                      className="w-full text-left p-2 rounded border border-blue-100 bg-white hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-blue-800">{result.bpiCode}</span>
                        {result.productName && (
                          <span className="text-xs text-blue-600">{result.productName}</span>
                        )}
                      </div>
                      {result.crewName && (
                        <p className="text-sm text-gray-700 mt-0.5">{result.crewName}</p>
                      )}
                      <div className="flex gap-1 mt-1">
                        {result.contacts.map((contact) => (
                          <Badge key={contact.id} variant="outline" className="text-xs">
                            {contact.first_name} {contact.last_name}
                          </Badge>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Aucun résultat (recherche par nom) */}
            {searchMode === 'crew_name' &&
              searched &&
              !bpiLoading &&
              searchInput.trim() &&
              !multipleResults?.found &&
              !selectedBpiResult && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium">Aucun BPI trouvé pour ce nom</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Essayez avec un autre terme ou passez à la recherche par code BPI.
                  </p>
                </div>
              )}

            {/* Résultat unique (recherche par code BPI ou sélection) */}
            {showSingleResult && displayResult && (
              <div className="mt-2">
                {displayResult.found ? (
                  <div className="rounded-md border border-green-200 bg-green-50 p-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {displayResult.contacts.length} contact
                        {displayResult.contacts.length > 1 ? 's' : ''} trouvé
                        {displayResult.contacts.length > 1 ? 's' : ''}
                        {displayResult.bpiCode && ` - ${displayResult.bpiCode}`}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {displayResult.contacts.map((contact) => (
                        <Badge key={contact.id} variant="outline" className="bg-white">
                          <Users className="h-3 w-3 mr-1" />
                          {contact.first_name} {contact.last_name}
                        </Badge>
                      ))}
                    </div>
                    {(displayResult.crewName || displayResult.productName) && (
                      <div className="mt-2 text-xs text-green-600 space-y-0.5">
                        {displayResult.crewName && (
                          <p>
                            <span className="font-medium">Équipage :</span> {displayResult.crewName}{' '}
                            <span className="text-green-500">(pré-rempli)</span>
                          </p>
                        )}
                        {displayResult.productName && (
                          <p>
                            <span className="font-medium">Produit :</span>{' '}
                            {displayResult.productName}{' '}
                            <span className="text-green-500">(pré-rempli)</span>
                          </p>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-green-600 mt-2">
                      Ces contacts seront automatiquement ajoutés à l'équipage
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        Aucun prospect trouvé pour ce code BPI
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      L'équipage sera créé sans membres associés. Vous pourrez les ajouter
                      manuellement.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Informations de base */}
          <div className="space-y-4">
            {/* items-start : le bloc numero est plus haut (bouton + message),
                sans quoi le champ nom se recentre verticalement. */}
            <div className="grid grid-cols-2 items-start gap-4">
              <FormInput
                control={control}
                label="Nom d'équipage"
                name="name"
                placeholder="Les Cigales du Désert"
              />

              <div className="space-y-2">
                <FormInput
                  control={control}
                  label="Numéro d'équipage"
                  name="crew_number"
                  placeholder="ex : 1717 (vide = auto)"
                />
                {/* Bouton sous le champ : cote a cote, le libelle ecrasait l'input
                    et faisait passer le label sur deux lignes. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={!productId || numberChecking}
                  onClick={handlePeekNext}
                >
                  {numberChecking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Prochain numéro dispo
                </Button>
                {numberDescription && (
                  <p
                    className={`text-xs ${
                      numberDescription.tone === 'error'
                        ? 'text-red-600'
                        : numberDescription.tone === 'warn'
                          ? 'text-amber-700'
                          : 'text-emerald-700'
                    }`}
                  >
                    {numberDescription.text}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={control}
                label="Statut"
                name="status"
                options={STATUS_SELECT_OPTIONS}
              />

              <FormSelect
                control={control}
                label="Probabilité"
                name="probability"
                required
                options={PROBABILITY_SELECT_OPTIONS}
              />
            </div>

            <FormSelect
              control={control}
              label="Produit"
              name="product_id"
              required
              placeholder="Sélectionnez un produit"
              options={products.map((product) => ({
                value: product.id,
                label: product.name,
              }))}
            />
          </div>

          {/* Relations */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={control}
                label="Véhicule"
                name="vehicle_id"
                options={[
                  { value: '', label: 'Aucun' },
                  ...vehicles.map((vehicle) => ({
                    value: vehicle.id,
                    label: vehicle.registration,
                  })),
                ]}
              />

              <FormSelect
                control={control}
                label="Association"
                name="association_id"
                options={[
                  { value: '', label: 'Aucune' },
                  ...associations.map((association) => ({
                    value: association.id,
                    label: association.name,
                  })),
                ]}
              />
            </div>

            <FormSelect
              control={control}
              label="Réduction"
              name="discount_id"
              options={[
                { value: '', label: 'Aucune' },
                ...discounts.map((discount) => ({
                  value: discount.id,
                  label: discount.name,
                })),
              ]}
            />
          </div>

          {/* Financement */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={control}
                label="Type de financement"
                name="financing_type"
                options={Object.values(CREW_FINANCING_TYPE).map((type) => ({
                  value: type,
                  label: CREW_FINANCING_TYPE_LABELS[type],
                }))}
              />

              <FormInput
                control={control}
                label="Budget (€)"
                name="budget"
                type="number"
                placeholder="8500"
                valueAsNumber
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <Controller
                control={control}
                name="has_derogation"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    onChange={field.onChange}
                    label="Dérogation"
                  />
                )}
              />

              <Controller
                control={control}
                name="organizes_pvd"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    onChange={field.onChange}
                    label="Organisation PVD"
                  />
                )}
              />
            </div>
          </div>

          {/* Motif Annulation ou Report */}
          <FormInput
            control={control}
            label="Motif Annulation ou Report"
            name="report"
            placeholder="Aucun"
          />

          {/* Notes */}
          <FormInput
            control={control}
            label="Notes"
            name="notes"
            placeholder="Notes sur l'équipage..."
          />
        </form>
      </FormProvider>
      <ConfirmDialog
        open={reservedConfirmation !== null}
        onOpenChange={(confirmationOpen) => {
          if (!confirmationOpen) setReservedConfirmation(null);
        }}
        title="Numéro réservé"
        description={
          reservedConfirmation
            ? `Le numéro ${reservedConfirmation.check.crew_number} est réservé${
                reservedConfirmation.check.reserved_reason
                  ? ` (${reservedConfirmation.check.reserved_reason})`
                  : ''
              }. Voulez-vous l’attribuer à cet équipage ?`
            : undefined
        }
        confirmLabel="Attribuer quand même"
        onConfirm={() => {
          const pending = reservedConfirmation;
          setReservedConfirmation(null);
          if (pending) {
            void methods.handleSubmit(() => submitCrew(pending.data, true))();
          }
        }}
      />
    </Modal>
  );
}
